/**
 * DynamicField — config-driven input renderer for the cold-start wizard.
 *
 * Switches on `field.kind` and dispatches to the matching primitive:
 *   amount    → AmountInput  (₹ prefix, optional suffix, live caption)
 *   percent   → PercentInput (% suffix)
 *   age_band  → OptionPick   (radio-card list)
 *   toggle    → TogglePill   (Yes/No pills + optional children)
 *
 * To add a new input kind: extend `FieldConfig` in types.ts and add a branch
 * here. No changes to the wizard step or form-state code are required.
 */
import * as React from 'react';
import type {
  FieldConfig, ProfileInput, ProfileNumericKey, ProfileBooleanKey,
} from '../types';
import { PALETTE_FOREST, PALETTE_AMBER } from '../tokens';

interface DynamicFieldProps {
  field:    FieldConfig;
  profile:  ProfileInput;
  onChange: <K extends keyof ProfileInput>(key: K, value: ProfileInput[K]) => void;
}

export function DynamicField({ field, profile, onChange }: DynamicFieldProps) {
  // Optional inline visibility predicate
  if (field.visibleIf && !field.visibleIf(profile)) return null;

  if (field.kind === 'amount') {
    return (
      <AmountInput
        field={field}
        value={profile[field.name] as number}
        profile={profile}
        onChange={(v) => onChange(field.name, v as ProfileInput[ProfileNumericKey])}
      />
    );
  }

  if (field.kind === 'percent') {
    return (
      <PercentInput
        field={field}
        value={profile[field.name] as number}
        onChange={(v) => onChange(field.name, v as ProfileInput[ProfileNumericKey])}
      />
    );
  }

  if (field.kind === 'age_band') {
    return (
      <OptionPick
        field={field}
        value={profile.age}
        onChange={(v) => onChange('age', v)}
      />
    );
  }

  if (field.kind === 'toggle') {
    return (
      <TogglePill
        field={field}
        value={profile[field.name] as boolean}
        onChange={(v) => onChange(field.name, v as ProfileInput[ProfileBooleanKey])}
        renderChildren={(children) =>
          children.map((child) => (
            <DynamicField key={child.id} field={child} profile={profile} onChange={onChange} />
          ))
        }
      />
    );
  }

  return null;
}

// ── Amount input (₹ prefix, optional suffix, live caption) ──────────────────

function AmountInput({
  field, value, profile, onChange,
}: {
  field:    Extract<FieldConfig, { kind: 'amount' }>;
  value:    number;
  profile:  ProfileInput;
  onChange: (v: number) => void;
}) {
  const liveCaption = field.liveCaption?.(value, profile);

  return (
    <div className="space-y-1.5">
      <FieldLabel label={field.label} sectionRef={field.sectionRef} />
      <div className="group flex items-center rounded-xl border border-border bg-background px-3.5 transition-all focus-within:border-foreground/20 focus-within:shadow-sm">
        <span className="font-mono text-sm text-muted-foreground pr-1.5 select-none">₹</span>
        <input
          type="number"
          inputMode="numeric"
          min={0}
          max={field.max}
          step={field.step ?? 1000}
          value={Number.isFinite(value) ? value : 0}
          onChange={(e) => {
            const next = Number(e.target.value);
            if (Number.isNaN(next)) return;
            onChange(field.max != null ? Math.min(next, field.max) : next);
          }}
          className="flex-1 min-w-0 bg-transparent border-0 outline-none py-2.5 text-base font-mono tabular-nums text-foreground"
        />
        {(liveCaption || field.suffix) && (
          <span className="text-[11px] font-medium text-muted-foreground tabular-nums whitespace-nowrap pl-2 shrink-0">
            {liveCaption || field.suffix}
          </span>
        )}
      </div>
      {field.hint && <p className="text-[11px] text-muted-foreground leading-relaxed">{field.hint}</p>}
    </div>
  );
}

// ── Percent input ──────────────────────────────────────────────────────────

function PercentInput({
  field, value, onChange,
}: {
  field:    Extract<FieldConfig, { kind: 'percent' }>;
  value:    number;
  onChange: (v: number) => void;
}) {
  return (
    <div className="space-y-1.5">
      <FieldLabel label={field.label} sectionRef={field.sectionRef} />
      <div className="group flex items-center rounded-xl border border-border bg-background px-3.5 transition-all focus-within:border-foreground/20 focus-within:shadow-sm">
        <input
          type="number"
          inputMode="decimal"
          min={0}
          max={field.max ?? 100}
          step={field.step ?? 1}
          value={Number.isFinite(value) ? value : 0}
          onChange={(e) => {
            const next = Number(e.target.value);
            if (Number.isNaN(next)) return;
            onChange(field.max != null ? Math.min(next, field.max) : next);
          }}
          className="flex-1 min-w-0 bg-transparent border-0 outline-none py-2.5 text-base font-mono tabular-nums text-foreground"
        />
        <span className="text-[11px] font-medium text-muted-foreground tabular-nums whitespace-nowrap pl-1 shrink-0">
          % {field.suffix ?? ''}
        </span>
      </div>
      {field.hint && <p className="text-[11px] text-muted-foreground leading-relaxed">{field.hint}</p>}
    </div>
  );
}

// ── Option pick (radio-card stack used for age band) ───────────────────────

function OptionPick({
  field, value, onChange,
}: {
  field:    Extract<FieldConfig, { kind: 'age_band' }>;
  value:    string;
  onChange: (v: ProfileInput['age']) => void;
}) {
  return (
    <div className="space-y-2">
      <FieldLabel label={field.label} sectionRef={field.sectionRef} />
      <div className="grid gap-2">
        {field.options.map((opt) => {
          const selected = value === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => onChange(opt.value)}
              aria-pressed={selected}
              className="group relative flex items-start gap-3 rounded-xl border bg-background px-3.5 py-3 text-left transition-all hover:border-foreground/20"
              style={{
                borderColor: selected ? PALETTE_FOREST : undefined,
                backgroundColor: selected ? `${PALETTE_FOREST}08` : undefined,
              }}
            >
              <span
                className="mt-0.5 grid place-items-center h-4 w-4 rounded-full border-2 shrink-0 transition-colors"
                style={{ borderColor: selected ? PALETTE_FOREST : 'hsl(var(--border))' }}
              >
                {selected && (
                  <span
                    className="block h-1.5 w-1.5 rounded-full"
                    style={{ backgroundColor: PALETTE_FOREST }}
                  />
                )}
              </span>
              <span className="flex-1 min-w-0">
                <span className="flex items-center gap-1.5">
                  <span className="text-sm font-medium text-foreground">{opt.label}</span>
                  {opt.badge && (
                    <span
                      className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
                      style={{
                        backgroundColor: opt.badge.tone === 'amber' ? `${PALETTE_AMBER}18` : `${PALETTE_FOREST}12`,
                        color:           opt.badge.tone === 'amber' ? PALETTE_AMBER          : PALETTE_FOREST,
                      }}
                    >
                      {opt.badge.text}
                    </span>
                  )}
                </span>
                {opt.sub && <span className="block text-[11px] text-muted-foreground mt-0.5">{opt.sub}</span>}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Toggle pill (Yes/No pair, optional children when on) ───────────────────

function TogglePill({
  field, value, onChange, renderChildren,
}: {
  field:    Extract<FieldConfig, { kind: 'toggle' }>;
  value:    boolean;
  onChange: (v: boolean) => void;
  renderChildren: (children: FieldConfig[]) => React.ReactNode;
}) {
  const trueLabel  = field.trueLabel  ?? 'Yes';
  const falseLabel = field.falseLabel ?? 'No';

  return (
    <div className="space-y-1.5">
      <FieldLabel label={field.label} sectionRef={field.sectionRef} />
      <div className="inline-flex rounded-xl border border-border bg-background p-1 gap-1">
        <PillButton active={value}  onClick={() => onChange(true)}>{trueLabel}</PillButton>
        <PillButton active={!value} onClick={() => onChange(false)}>{falseLabel}</PillButton>
      </div>
      {field.hint && <p className="text-[11px] text-muted-foreground leading-relaxed">{field.hint}</p>}

      {value && field.children && field.children.length > 0 && (
        <div className="mt-3 pl-3 border-l-2 space-y-4 animate-slide-down-fade origin-top"
          style={{ borderColor: `${PALETTE_FOREST}25` }}
        >
          {renderChildren(field.children)}
        </div>
      )}
    </div>
  );
}

function PillButton({
  active, onClick, children,
}: {
  active:   boolean;
  onClick:  () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className="px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all"
      style={{
        backgroundColor: active ? PALETTE_FOREST : 'transparent',
        color:           active ? 'white'         : 'hsl(var(--muted-foreground))',
      }}
    >
      {children}
    </button>
  );
}

// ── Shared label sub-component ─────────────────────────────────────────────

function FieldLabel({ label, sectionRef }: { label: string; sectionRef?: string }) {
  return (
    <label className="block text-xs font-semibold text-foreground tracking-tight">
      {label}
      {sectionRef && (
        <span
          className="ml-1.5 text-[9px] font-mono font-bold uppercase tracking-widest px-1 py-0.5 rounded"
          style={{ backgroundColor: `${PALETTE_FOREST}10`, color: PALETTE_FOREST }}
        >
          {sectionRef}
        </span>
      )}
    </label>
  );
}
